import { useEffect } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { CallOutgoing02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant, Modal } from "shared/components";
import {
  useSoftphone,
  LIVE_CALL_STATUSES,
  type SoftphoneStatus
} from "softphone";
import { PRE_DIAL_MODAL_Z_INDEX } from "softphone/stacking";
import { CallNumberFields } from "./CallNumberFields";
import {
  NO_CALLABLE_NUMBERS_REASON,
  useCallSelection
} from "./useCallSelection";

// Why the dial button is disabled, in the user's terms. `isReady` is the AND of
// two independent things — the device being registered and no call being live —
// so the copy has to distinguish "wait a moment" from "this needs you".
// The silent default must be the EXPLANATION, not silence: `isReady` is also
// false during a transport blip and while a fault is deferred behind a card,
// and in both of those the status still reads as something benign.
const describeUnavailable = (
  status: SoftphoneStatus,
  errorMessage: string | null
): string | null => {
  // A call is live or ringing — the softphone card already says so.
  if (LIVE_CALL_STATUSES.includes(status)) return null;
  if (status === "mic-denied" || status === "error")
    return errorMessage ?? "The voice service is unavailable right now.";
  return "Reconnecting to the voice service — this takes a moment.";
};

interface MakeCallModalProps {
  opened: boolean;
  onClose: () => void;
  // CRM contact id used to load the destination phones. May be "" when the
  // caller supplies `toPhones`/`toName` directly (e.g. a lead with no contact).
  contactId: string;
  leadId?: number;
  customerId?: number;
  // Destination phones/name when there's no CRM contact to derive them from.
  toPhones?: string[];
  toName?: string;
  // Pre-fill the picker with the caller's inline selection (composer hand-off).
  // When omitted (e.g. the retry path) the picker seeds its own defaults.
  initialFromId?: number;
  initialToNumber?: string;
}

/**
 * Pre-dial confirmation: confirm which provisioned (voice-capable) number to
 * call FROM and which of the contact's phones to call TO, then place the call.
 */
export const MakeCallModal = ({
  opened,
  onClose,
  contactId,
  leadId,
  customerId,
  toPhones,
  toName,
  initialFromId,
  initialToNumber
}: MakeCallModalProps) => {
  const { placeCall, isReady, status, errorMessage } = useSoftphone();
  const selection = useCallSelection(
    contactId,
    opened,
    toPhones,
    toName,
    leadId
  );
  const {
    fromId,
    toNumber,
    fromLabel,
    contactName,
    setFromId,
    setToNumber,
    callableFromOptions,
    noCallableNumbers
  } = selection;

  // On open, seed the picker from the caller's inline selection (if handed in);
  // on close, clear so the next open re-seeds from props or defaults.
  useEffect(() => {
    if (opened) {
      if (initialFromId !== undefined) setFromId(initialFromId);
      if (initialToNumber !== undefined) setToNumber(initialToNumber);
    } else {
      setFromId(undefined);
      setToNumber(undefined);
    }
  }, [opened, initialFromId, initialToNumber, setFromId, setToNumber]);

  const fromCallable = callableFromOptions.some((n) => n.id === fromId);
  const canCall = fromCallable && !!toNumber && isReady;
  // The softphone recovers from short network faults silently now (CRM-1073),
  // so the dial button can be disabled with nothing on screen explaining why.
  // This is the moment the user actually cares, so say it here.
  const softphoneUnavailableReason = isReady
    ? null
    : describeUnavailable(status, errorMessage);
  const unavailableReason = noCallableNumbers
    ? NO_CALLABLE_NUMBERS_REASON
    : softphoneUnavailableReason;

  const handleCall = () => {
    if (!fromId || !fromCallable || !toNumber) return;
    placeCall({
      toNumber,
      fromNumberId: fromId,
      leadId,
      // The conversation's contact pins the call's identity on the server, so
      // the call row and this thread name the same person.
      contactId: contactId ? Number(contactId) : undefined,
      customerId,
      fromLabel,
      toLabel: contactName || undefined
    });
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      // This modal is launched from the inbox Details drawer, which outranks the
      // shared Modal's default z-index — see softphone/stacking.
      zIndex={PRE_DIAL_MODAL_Z_INDEX}
      panelClassName="w-full max-w-[420px] flex flex-col"
    >
      <div className="flex flex-col items-center gap-4 px-6 pt-6">
        <div className="flex size-16 items-center justify-center rounded-full bg-alpha-40">
          <HugeiconsIcon
            icon={CallOutgoing02Icon}
            size={24}
            className="text-content-1000"
          />
        </div>
        <h2 className="text-lg font-bold text-content-1000">Make a call</h2>
      </div>

      <div className="px-6 py-6">
        <CallNumberFields selection={selection} />
      </div>

      <div className="flex flex-col gap-2 px-6 pb-6">
        {unavailableReason ? (
          <p className="text-sm text-content-500">{unavailableReason}</p>
        ) : null}
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          onClick={handleCall}
          disabled={!canCall}
        >
          Make a call
        </Button>
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </Modal>
  );
};
